import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import type { ChannelFilters, ChannelOptions, ChannelSort, Event } from 'stream-chat'
import { Chat, Channel } from 'stream-chat-react'
import { useChatContext } from 'stream-chat-react'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import uniq from 'lodash/uniq'
import { isMobileOnly, isTablet } from 'mobile-device-detect'
import { useUpdateAppHeightOnResize } from './hooks/useUpdateAppHeightOnResize'
import { useConnectUser } from 'components/Chat/hooks/useConnectUser'
import UserProfile from 'components/Chat/components/UserProfile'
import {
  CreateChannel,
  CustomMessage,
  MessagingInput,
  MessagingThreadHeader,
  UserSelection,
  GroupCreation,
  ChatEmptyState,
  ChatLoadingState,
} from 'components/Chat/components'
import { ChannelInner } from 'components/Chat/components/ChannelInner/ChannelInner'
import ChannelListHeader from 'components/Chat/components/ChannelListHeader'
import { GiphyContextProvider } from './Giphy'
import { streamChatI18n } from 'i18n/streamChatI18n'
import { useLanguage } from 'i18n/useLanguage'
import { APP_STREAM_KEY } from 'config'
import { StreamChatGenerics } from 'components/Chat/types'
import '@stream-io/stream-chat-css/dist/css/index.css'
import './App.css'
import { ChatWrapper } from './style'
import { IChatAttachment } from '.'
import { replaceState } from 'utils/helper'
import { useTenantId } from 'utils/useTenantId'
import { isGroupChannel } from 'components/Chat/utils/channelHelpers'

// Longest wait for the conversation list: the Stream request timeout (6s, see useConnectUser) plus a margin
const CHANNEL_LIST_TIMEOUT_MS = 7500

type AppProps = {
  token: string
  userId: string
  apiKey: string
  chatUsers: any
  usersAssessmentResponseByQuestion: any
  chatUnreadMessageCountAction: any
  chatUnreadMessageCount: number
  getUsersAssessmentResponseByQuestionAction: (data: any) => {}
  userToConnect: { id: string; name?: string; image?: string }
  saveUserFileAction: (streamMsgId: string, attachment: IChatAttachment) => void
  targetOrigin: string
  channelListOptions: {
    options: ChannelOptions
    filters: ChannelFilters
    sort: ChannelSort
  }
}

const ChatApplication = (props: AppProps) => {
  const { userId, token, chatUsers } = props
  const { activeMemberId } = useParams()
  const { language } = useLanguage()
  // Capture initial language once so the connection is stable. Subsequent
  // language changes are pushed via partialUpdateUser below to avoid a full
  // reconnect on every language switch.
  const [chatUser] = useState(() => ({ id: userId, language }))
  const theme = 'light'
  const width = window.screen.width
  const chatClient = useConnectUser<StreamChatGenerics>(APP_STREAM_KEY, chatUser, token)

  useUpdateAppHeightOnResize()

  // Keep Stream Chat's server-side user.language in sync so auto-translation
  // (when enabled on the app) delivers the `${lang}_text` field for this viewer.
  // Also re-query already-watched channels so any messages whose `i18n` was
  // back-filled after the initial fetch (the all-same-language case) pick up
  // the newly available translations without a full page reload.
  useEffect(() => {
    if (!chatClient || !language) return
    if ((chatClient.user as { language?: string } | undefined)?.language === language) return
    chatClient
      .partialUpdateUser({ id: userId, set: { language } })
      .then(() =>
        Promise.all(
          Object.values(chatClient.activeChannels || {}).map((channel) =>
            channel.query({ messages: { limit: 30 } }).catch(() => undefined)
          )
        )
      )
      .catch((e) => console.error('Failed to update Stream Chat user.language', e))
  }, [chatClient, language, userId])

  if (!chatClient) {
    // Show that chat is loading until the connection to the backend is established
    // (at about the same height as the loading state of the conversation pane that follows)
    return (
      <div className="flex justify-center mt-[15%]">
        <ChatLoadingState />
      </div>
    )
  }

  return (
    // Below 1000px fill the chat area so the conversation pane gets a fixed height and its message list scrolls
    <ChatWrapper className="h-full mdl:h-auto">
      <div className="h-full mdl:h-auto">
        {/* <div> TOTAL: {chatClient?.user?.total_unread_count}</div> */}

        <Chat client={chatClient} i18nInstance={streamChatI18n} theme={`messaging ${theme}`}>
          <MyChat
            theme={theme}
            activeMemberId={activeMemberId}
            width={width}
            usersAssessmentResponseByQuestion={props.usersAssessmentResponseByQuestion}
            getUsersAssessmentResponseByQuestionAction={
              props.getUsersAssessmentResponseByQuestionAction
            }
            userId={userId}
            chatUsers={chatUsers}
            chatUnreadMessageCountAction={props.chatUnreadMessageCountAction}
            chatUnreadMessageCount={props.chatUnreadMessageCount}
            saveUserFileAction={props.saveUserFileAction}
          />
        </Chat>
      </div>
    </ChatWrapper>
  )
}

export default ChatApplication

const MyChat = ({
  usersAssessmentResponseByQuestion,
  getUsersAssessmentResponseByQuestionAction,
  theme,
  width,
  activeMemberId,
  userId,
  chatUsers,
  chatUnreadMessageCountAction,
  chatUnreadMessageCount,
  saveUserFileAction,
}) => {
  const context = useChatContext<StreamChatGenerics>()
  const tenantId = useTenantId()
  const routerHistory = useHistory()
  const [membersList, setMembersList] = useState([])
  // Each navigation to /chat/:activeMemberId (e.g. every notification click) opens that member's chat once
  const linkKey = activeMemberId ? `${routerHistory?.location.key}:${activeMemberId}` : undefined
  const handledLinkKeyRef = useRef<string | undefined>()
  // Link still being opened; cleared when the user picks a conversation themselves
  const openingLinkKeyRef = useRef<string | undefined>()
  // True while the conversation for the URL member is being found/created
  const [isOpeningMember, setIsOpeningMember] = useState(!!activeMemberId)
  const [showChannelCreate, setShowChannelCreate] = useState(false)
  const [showChannelPreview, setShowChannelPreview] = useState(true)
  const [showDesktopUserProfile, setShowDesktopUserProfile] = useState(false)
  const [activeMemberChatChannelExists, setActiveMemberChannelExists] = useState<boolean>(false)
  const [showUserSelection, setShowUserSelection] = useState(false)
  const [showGroupCreation, setShowGroupCreation] = useState(false)
  const [initialJumpMessageId, setInitialJumpMessageId] = useState<string | null>(null)
  // When we auto-create a channel from URL, pass it explicitly so Channel subscribes to message updates
  const [programmaticChannel, setProgrammaticChannel] = useState<any>(null)
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  // Clear programmaticChannel when user selects a different channel from the list
  useEffect(() => {
    if (programmaticChannel && context.channel && context.channel.id !== programmaticChannel.id) {
      setProgrammaticChannel(null)
    }
  }, [context.channel, programmaticChannel])

  // Going back to the conversation list (mobile back arrow) closes the open conversation
  const previousChannelRef = useRef(context.channel)
  useEffect(() => {
    if (previousChannelRef.current && !context.channel) setProgrammaticChannel(null)
    previousChannelRef.current = context.channel
  }, [context.channel])
  // Once a conversation has been open, the list must not reopen the latest one by itself after it is closed
  // (set while rendering so the list already sees it in the render where the conversation closes)
  const [hadConversation, setHadConversation] = useState(false)
  if (context.channel && !hadConversation) setHadConversation(true)

  // Track when initial channel loading is complete
  useEffect(() => {
    if (!isInitialLoading || !context.client) {
      return () => {}
    }

    // If a channel is actively selected, end loading immediately
    if (context.channel) {
      setIsInitialLoading(false)
      return () => {}
    }

    // Otherwise keep loading until the conversation list has been fetched (however slow the network),
    // so people who have conversations never see the "start a conversation" state meanwhile
    let openTimer: ReturnType<typeof setTimeout> | undefined
    const listLoaded = context.client.on('channels.queried', (event) => {
      clearTimeout(openTimer)
      if (!event.queriedChannels?.channels.length) {
        setIsInitialLoading(false)
      } else {
        // The list opens the latest conversation right away; stop waiting if it doesn't
        openTimer = setTimeout(() => setIsInitialLoading(false), 1000)
      }
    })
    // Stop waiting if the list request fails
    const failTimer = setTimeout(() => setIsInitialLoading(false), CHANNEL_LIST_TIMEOUT_MS)

    return () => {
      listLoaded.unsubscribe()
      clearTimeout(openTimer)
      clearTimeout(failTimer)
    }
  }, [isInitialLoading, context.client, context.channel])

  // When I leave the open conversation or am removed from it, close it (the list already drops it)
  // so its history and composer are no longer shown
  const { setActiveChannel } = context
  useEffect(() => {
    const closeIfOpen = (event: Event<StreamChatGenerics>) => {
      if (event.type === 'member.removed' && event.user?.id !== context.client.userID) return
      const cid = event.cid ?? event.channel?.cid
      if (!cid) return
      if (context.channel?.cid === cid) setActiveChannel(undefined)
      setProgrammaticChannel((current) => (current?.cid === cid ? null : current))
    }
    const subscriptions = [
      context.client.on('member.removed', closeIfOpen),
      context.client.on('notification.removed_from_channel', closeIfOpen),
    ]
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [context.client, context.channel, setActiveChannel])

  // Create a wrapper component for MessagingInput that has access to showChannelCreate
  const MessagingInputWrapper = useCallback(
    (props: any) => {
      return <MessagingInput {...props} showChannelCreate={showChannelCreate} />
    },
    [showChannelCreate]
  )

  useEffect(() => {
    if (!isEmpty(context.client.activeChannels)) {
      const members: any = []
      const channels = Object.values(context.client.activeChannels)

      for (let i = 0; i < channels.length; i++) {
        const channelMember: any = Object.values(get(channels[i], 'state.members', {})).filter(
          (f: any) => f.user.id !== context.client.userID
        )
        if (channelMember.length === 1) {
          members.push(channelMember[0].user_id)
        }
      }
      uniq(members)

      if (JSON.stringify(membersList.sort()) !== JSON.stringify(members.sort())) {
        setMembersList(members)
      }
    }

    // eslint-disable-next-line
  }, [context])

  useEffect(() => {
    if (membersList.length > 0) {
      getUsersAssessmentResponseByQuestionAction({
        question: 'isIndustry',
        type: 'initial',
        users: membersList,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersList])

  // Open (or create) the 1-1 conversation for /chat/:activeMemberId. Runs once per navigation,
  // so a later link (e.g. a notification clicked while already on the chat page) opens too,
  // and does not depend on the user already having other 1-1 channels.
  useEffect(() => {
    if (linkKey && handledLinkKeyRef.current !== linkKey) {
      handledLinkKeyRef.current = linkKey
      openingLinkKeyRef.current = linkKey
      // Only the latest link may change the open conversation
      const isLatest = () => openingLinkKeyRef.current === linkKey
      setIsOpeningMember(true)
      const filter = { type: 'messaging', members: { $eq: [activeMemberId, userId] } }
      context.client
        .queryChannels(filter)
        .then(async (channels) => {
          if (!isLatest()) return
          // $eq also matches groups with the same two members; the link means the 1-1 chat
          const directChannel = channels?.find((channel) => !isGroupChannel(channel))
          if (directChannel) {
            setActiveMemberChannelExists(true)
            // Only set active channel if no channel is currently active or if it's different
            if (!context.channel || context.channel.id !== directChannel.id) {
              context.setActiveChannel(directChannel)
            }
            setShowChannelPreview(true)
            setIsOpeningMember(false)
          } else {
            // No existing channel - create one automatically
            setActiveMemberChannelExists(false)
            try {
              const customData = {
                members: [activeMemberId, context.client.userID || 'unknown'],
                team: tenantId, // Same tenant the channel list filters on (as UserSelection)
                distinct: true, // Ensures same members map to same channel
                isGroup: false,
              }
              const newChannel = context.client.channel('messaging', customData)
              await newChannel.create()
              await newChannel.watch()

              // Small delay so channel state is fully initialized (same as UserSelection flow)
              await new Promise((resolve) => setTimeout(resolve, 100))
              if (!isLatest()) return

              // Use channel from client.activeChannels so we get the same reference used for real-time message updates
              const channelFromClient = context.client.activeChannels[newChannel.cid] ?? newChannel

              setShowChannelPreview(true)
              // Pass channel explicitly so Channel component subscribes to message updates; defer setActiveChannel for context
              setProgrammaticChannel(channelFromClient)
              setTimeout(() => {
                context.setActiveChannel(channelFromClient)
                setIsOpeningMember(false)
              }, 0)
            } catch (error) {
              console.error('Failed to create channel:', error)
              if (!isLatest()) return
              // Fallback to showing CreateChannel component on error
              setShowChannelCreate(true)
              setIsOpeningMember(false)
            }
          }
        })
        .catch((error) => {
          console.error('Failed to open chat from link:', error)
          if (isLatest()) setIsOpeningMember(false)
        })
      replaceState(document.title)
    }
  }, [activeMemberId, context, linkKey, tenantId, userId])

  // Below 1000px only one pane fits: the open conversation, or the conversation list
  const showConversationPane =
    (showChannelCreate || !!context.channel || !!programmaticChannel) && !showUserSelection

  return (
    <div className="flex w-full h-full overflow-hidden">
      {/* Channel List - Left Side */}
      <div
        className={`
        w-full
        mdl:w-1/4
        lg:w-1/5
        xl:w-1/4
        ${showConversationPane ? 'hidden' : 'flex'}
        mdl:block
        border-r border-app-grey-25
        h-full
        overflow-hidden
        flex-col
      `}
      >
        {showUserSelection ? (
          <UserSelection
            onBack={() => setShowUserSelection(false)}
            onUserSelect={(channel) => {
              // Close selection first to avoid ChannelList auto-select race
              setShowUserSelection(false)
              setShowChannelPreview(true)
              // Stop a link that is still opening from overriding the user's pick
              openingLinkKeyRef.current = undefined
              // Defer activation slightly so ChannelList renders with no auto-pick
              setTimeout(() => {
                context.setActiveChannel(channel)
                setIsOpeningMember(false)
              }, 0)
            }}
            chatUsers={chatUsers}
          />
        ) : (
          <ChannelListHeader
            userId={userId}
            showChannelCreate={showChannelCreate}
            setShowChannelPreview={setShowChannelPreview}
            showChannelPreview={showChannelPreview}
            setShowChannelCreate={setShowChannelCreate}
            usersAssessmentResponseByQuestion={usersAssessmentResponseByQuestion}
            chatUnreadMessageCountAction={chatUnreadMessageCountAction}
            chatUnreadMessageCount={chatUnreadMessageCount}
            setShowUserSelection={setShowUserSelection}
            showUserSelection={showUserSelection}
            setShowGroupCreation={setShowGroupCreation}
            showGroupCreation={showGroupCreation}
            setInitialJumpMessageId={setInitialJumpMessageId}
            // Don't auto-open the latest channel while a linked conversation is opening,
            // or after the user closed the open one
            isSelectingChannel={isOpeningMember || hadConversation}
          />
        )}
      </div>

      {/* Main Content Area - Right Side */}
      <div
        className={`
        chat-conversation-pane
        ${showDesktopUserProfile ? 'xl:w-1/2' : 'xl:w-3/4'}
        w-full
        mdl:w-3/4
        lg:w-4/5
        xl:w-3/4
        ${showConversationPane ? 'flex' : 'hidden'}
        mdl:block
        m-0
        h-full
        overflow-hidden
        flex-col
        `}
      >
        {!showChannelCreate && !context.channel && !programmaticChannel ? (
          <div className="h-full flex items-center justify-center mt-[20%]">
            {isInitialLoading || isOpeningMember ? (
              <ChatLoadingState />
            ) : (
              <ChatEmptyState onStartNewChat={() => setShowUserSelection(true)} />
            )}
          </div>
        ) : (
          <Channel
            channel={programmaticChannel || undefined}
            Input={MessagingInputWrapper}
            maxNumberOfFiles={10}
            Message={CustomMessage}
            multipleUploads={true}
            acceptedFiles={['*']}
            ThreadHeader={MessagingThreadHeader}
            TypingIndicator={() => null}
          >
            {showChannelCreate && (
              <GiphyContextProvider>
                <CreateChannel
                  chatUsers={chatUsers}
                  onClose={() => setShowChannelCreate(false)}
                  setShowChannelPreview={setShowChannelPreview}
                  showChannelPreview={showChannelPreview}
                  setShowChannelCreate={setShowChannelCreate}
                  showChannelCreate={showChannelCreate}
                  initialUserIds={
                    activeMemberId && !activeMemberChatChannelExists ? [activeMemberId] : []
                  }
                  setActiveMemberChannelExists={setActiveMemberChannelExists}
                />
              </GiphyContextProvider>
            )}
            {!showChannelCreate && (context.channel || programmaticChannel) && (
              <GiphyContextProvider>
                <ChannelInner
                  setShowDesktopUserProfile={setShowDesktopUserProfile}
                  showDesktopUserProfile={showDesktopUserProfile}
                  theme={theme}
                  setShowChannelPreview={setShowChannelPreview}
                  showChannelPreview={showChannelPreview}
                  usersAssessmentResponseByQuestion={usersAssessmentResponseByQuestion}
                  chatUnreadMessageCountAction={chatUnreadMessageCountAction}
                  saveUserFileAction={saveUserFileAction}
                  showChannelCreate={showChannelCreate}
                  initialJumpMessageId={initialJumpMessageId}
                  setInitialJumpMessageId={setInitialJumpMessageId}
                />
              </GiphyContextProvider>
            )}
          </Channel>
        )}
      </div>
      {!isMobileOnly && !isTablet && showDesktopUserProfile && (
        <UserProfile usersAssessmentResponseByQuestion={usersAssessmentResponseByQuestion} />
      )}

      {/* Group Creation Modal */}
      {showGroupCreation && (
        <GroupCreation
          onClose={() => setShowGroupCreation(false)}
          onGroupCreated={(channel) => {
            setShowGroupCreation(false)
            setShowChannelPreview(true)
            context.setActiveChannel(channel)
          }}
          chatUsers={chatUsers}
        />
      )}
    </div>
  )
}
